/* global google */

/*
 * Copyright (c) 2017 Ken Hibino.
 * Licensed under the MIT License (MIT).
 * See https://kenny-hibino.github.io/react-places-autocomplete
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import defaultStyles from './defaultStyles';

import styles from './styles.module.scss';

const cx = classNames.bind(styles);

class PlacesAutocomplete extends Component {
  constructor(props) {
    super(props);

    this.state = { autocompleteItems: [] };

    this.autocompleteCallback = this.autocompleteCallback.bind(this);
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
  }

  componentDidMount() {
    if (typeof google !== 'object') {
      return;
    }
    this.autocompleteService = new google.maps.places.AutocompleteService();
    this.autocompleteOK = google.maps.places.PlacesServiceStatus.OK;

    if (this.props.region) {
      this.bounds = this._getBounds();
    }
  }

  autocompleteCallback(predictions, status) {
    if (status != this.autocompleteOK) {
      this.props.onError(status);
      if (this.props.clearItemsOnError) {
        this.clearAutocomplete();
      }
      return;
    }

    this.setState({
      autocompleteItems: predictions.map((p, idx) => ({
        suggestion: p.description,
        placeId: p.place_id,
        active: false,
        index: idx,
        formattedSuggestion: this._formattedSuggestion(p.structured_formatting),
      })),
    });
  }

  _getBounds(autocomplete) {
    const { bounds } = this.props.region.geometry;
    if (bounds && typeof bounds.east === 'number') {
      return new google.maps.LatLngBounds(
        new google.maps.LatLng(bounds.east, bounds.south),
        new google.maps.LatLng(bounds.west, bounds.north)
      );
    }
  }

  _formattedSuggestion(structured_formatting) {
    return {
      mainText: structured_formatting.main_text,
      secondaryText: structured_formatting.secondary_text,
    };
  }

  clearAutocomplete() {
    this.setState({ autocompleteItems: [] });
  }

  selectAddress(address, placeId) {
    this.clearAutocomplete();
    this._handleSelect(address, placeId);
  }

  _handleSelect(address, placeId) {
    this.props.onSelect ? this.props.onSelect(address, placeId) : this.props.onChange(address);
  }

  _getActiveItem() {
    return this.state.autocompleteItems.find(item => item.active);
  }

  _selectActiveItemAtIndex(index) {
    const activeName = this.state.autocompleteItems.find(item => item.index === index).suggestion;
    this._setActiveItemAtIndex(index);
    this.props.onChange(activeName);
  }

  _handleEnterKey() {
    const activeItem = this._getActiveItem();
    if (activeItem === undefined) {
      this._handleEnterKeyWithoutActiveItem();
    } else {
      this.selectAddress(activeItem.suggestion, activeItem.placeId);
    }
  }

  _handleEnterKeyWithoutActiveItem() {
    if (this.props.onEnterKeyDown) {
      this.props.onEnterKeyDown(this.props.value);
      this.clearAutocomplete();
    } else {
      // noop
    }
  }

  _handleDownKey() {
    const activeItem = this._getActiveItem();
    if (activeItem === undefined) {
      this._selectActiveItemAtIndex(0);
    } else {
      const nextIndex = (activeItem.index + 1) % this.state.autocompleteItems.length;
      this._selectActiveItemAtIndex(nextIndex);
    }
  }

  _handleUpKey() {
    const activeItem = this._getActiveItem();
    if (activeItem === undefined) {
      this._selectActiveItemAtIndex(this.state.autocompleteItems.length - 1);
    } else {
      let prevIndex;
      if (activeItem.index === 0) {
        prevIndex = this.state.autocompleteItems.length - 1;
      } else {
        prevIndex = (activeItem.index - 1) % this.state.autocompleteItems.length;
      }
      this._selectActiveItemAtIndex(prevIndex);
    }
  }

  handleInputKeyDown(event) {
    const ARROW_UP = 38;
    const ARROW_DOWN = 40;
    const ENTER_KEY = 13;
    const ESC_KEY = 27;

    switch (event.keyCode) {
      case ENTER_KEY:
        event.preventDefault();
        this._handleEnterKey();
        break;
      case ARROW_DOWN:
        this._handleDownKey();
        break;
      case ARROW_UP:
        this._handleUpKey();
        break;
      case ESC_KEY:
        this.clearAutocomplete();
        break;
    }
  }

  _setActiveItemAtIndex(index) {
    this.setState({
      autocompleteItems: this.state.autocompleteItems.map((item, idx) => {
        if (idx === index) {
          return { ...item, active: true };
        }
        return { ...item, active: false };
      }),
    });
  }

  handleInputChange(event) {
    this.props.onChange(event.target.value);
    if (!event.target.value) {
      this.clearAutocomplete();
      return;
    }
    const boundsobj = this.bounds ? { bounds: this.bounds } : {};
    this.autocompleteService.getPlacePredictions(
      { ...this.props.options, boundsobj, input: event.target.value },
      this.autocompleteCallback
    );
  }

  autocompleteItemStyle(active) {
    const { styles = {} } = this.props || {};
    if (active) {
      return { ...defaultStyles.autocompleteItemActive, ...styles.autocompleteItemActive };
    }
    return {};
  }

  renderAutocomplete() {
    const { autocompleteItems } = this.state;
    const { styles = {} } = this.props || {};
    if (autocompleteItems.length === 0) {
      return null;
    }
    return (
      <div
        id="PlacesAutocomplete__autocomplete-container"
        className={cx(
          'places-autocomplete-wrap',
          this.props.classNames.autocompleteContainer || ''
        )}
        style={{ ...defaultStyles.autocompleteContainer, ...styles.autocompleteContainer }}
      >
        {autocompleteItems.map((p, idx) => (
          <div
            className={cx('autocomplete-item')}
            key={p.placeId}
            onMouseOver={() => this._setActiveItemAtIndex(p.index)}
            onMouseDown={() => this.selectAddress(p.suggestion, p.placeId)}
            style={{
              ...defaultStyles.autocompleteItem,
              ...styles.autocompleteItem,
              ...this.autocompleteItemStyle(p.active),
            }}
          >
            {this.props.autocompleteItem({
              suggestion: p.suggestion,
              formattedSuggestion: p.formattedSuggestion,
            })}
          </div>
        ))}
      </div>
    );
  }

  renderInput() {
    const {
      classNames,
      placeholder,
      styles = {},
      value,
      autoFocus,
      style,
      disabled,
    } = this.props || {};
    return (
      <input
        type="text"
        placeholder={placeholder}
        className={cx('places-input', classNames.input || '')}
        value={value}
        onChange={this.handleInputChange}
        onKeyDown={this.handleInputKeyDown}
        onBlur={() => this.clearAutocomplete()}
        style={{ ...styles.input, ...style }}
        autoFocus={autoFocus}
        disabled={disabled}
      />
    );
  }

  render() {
    if (typeof google !== 'object') {
      return <div className="text-muted py-2">Error - no internet connection.</div>;
    }
    const { classNames, style, loader } = this.props;
    return (
      <div
        style={{ ...defaultStyles.root, ...style }}
        className={cx('root', classNames.root || '', { loading: loader })}
      >
        {this.props.label ? <p className={cx('places-label')}>{this.props.label}</p> : null}
        {this.renderInput()}
        {loader ? <span className={cx('loader')}>{loader}</span> : null}
        {this.renderAutocomplete()}
      </div>
    );
  }
}

PlacesAutocomplete.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  clearItemsOnError: PropTypes.bool,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  autocompleteItem: PropTypes.func,
  classNames: PropTypes.shape({
    root: PropTypes.string,
    input: PropTypes.string,
    autocompleteContainer: PropTypes.string,
  }),
  styles: PropTypes.shape({
    root: PropTypes.object,
    input: PropTypes.object,
    autocompleteContainer: PropTypes.object,
    autocompleteItem: PropTypes.object,
    autocompleteItemActive: PropTypes.object,
  }),
  options: PropTypes.shape({
    bounds: PropTypes.object,
    componentRestrictions: PropTypes.object,
    location: PropTypes.object,
    offset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    types: PropTypes.array,
  }),
};

PlacesAutocomplete.defaultProps = {
  clearItemsOnError: false,
  onError: status =>
    console.error(
      '[react-places-autocomplete]: error happened when fetching data from Google Maps API.\nPlease check the docs here (https://developers.google.com/maps/documentation/javascript/places#place_details_responses)\nStatus: ',
      status
    ),
  placeholder: 'Address',
  autoFocus: false,
  classNames: {},
  autocompleteItem: ({ suggestion }) => <div>{suggestion}</div>,
  styles: {},
  options: {},
};

export default PlacesAutocomplete;
